import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Mail, 
  Phone, 
  Send, 
  CheckCircle, 
  Clock, 
  Users, 
  BookOpen, 
  MessageCircle, 
  Headphones, 
  Shield, 
  Zap 
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Extend Window interface for HubSpot
declare global {
  interface Window {
    HubSpotConversations?: {
      widget: {
        open: () => void;
        close: () => void;
        load: () => void;
      };
    };
    hsConversationsSettings?: {
      loadImmediately?: boolean;
    };
  }
}

const faqs = [
  {
    question: "How do I contact support?",
    answer: "You can reach our support team via the contact form below, email, or phone. We are here to help you 24/7."
  },
  {
    question: "What is the response time?",
    answer: "Our average response time is under 2 hours for all support requests."
  },
  {
    question: "Do you offer onboarding assistance?",
    answer: "Yes, our team provides onboarding and training for all new customers."
  },
  {
    question: "Where can I find documentation?",
    answer: "Comprehensive documentation is available at docs.ommni.io with guides, API references, and tutorials."
  },
  {
    question: "What support channels do you offer?",
    answer: "We offer email support, phone support, live chat, and a comprehensive knowledge base."
  },
  {
    question: "Do you provide technical integration support?",
    answer: "Yes, our technical team can assist with API integrations, platform setup, and custom configurations."
  }
];

const supportFeatures = [
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "Round-the-clock support whenever you need assistance"
  },
  {
    icon: Zap,
    title: "Fast Response",
    description: "Average response time under 2 hours for all inquiries"
  },
  {
    icon: Users,
    title: "Expert Team",
    description: "Dedicated specialists with deep platform knowledge"
  },
  {
    icon: Shield,
    title: "Priority Support",
    description: "Enterprise customers get priority queue access"
  }
];

export default function Support() {
  useSEO({
    title: 'Support Center - 24/7 Help & Customer Service | Ommni',
    description: 'Get help from Ommni\'s expert support team 24/7. Contact us via email, phone, or live chat. Average response time under 2 hours for all support requests.',
    keywords: 'customer support, technical support, help center, contact support, 24/7 support, commerce platform help',
    canonical: 'https://ommni.io/support',
  });

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("us");

  const phoneNumbers = {
    us: { number: "+1 512-200-4779", display: "+1 512-200-4779", label: "United States" },
    au: { number: "+61 4 0076 9964", display: "+61 4 0076 9964", label: "Australia" }
  };

  useEffect(() => {
    // Load HubSpot chat widget
    const script = document.createElement('script');
    script.src = '//js.hs-scripts.com/YOUR_PORTAL_ID.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleOpenChat = () => {
    // Open HubSpot chat widget
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.open();
    } else if (window.hsConversationsSettings) {
      // Fallback if widget isn't loaded yet
      window.hsConversationsSettings.loadImmediately = true;
      setTimeout(() => {
        if (window.HubSpotConversations) {
          window.HubSpotConversations.widget.open();
        }
      }, 1000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    toast({
      title: "Message Sent!",
      description: "Our support team will get back to you within 2 hours.",
    });
    // Here you would handle sending the form data to your backend
  };

  return (
    <div className="min-h-screen">
      {/* JSON-LD FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <main className="pt-32 px-4 pb-24 bg-gradient-to-b from-primary/5 to-background" role="main">
        {/* Hero Section */}
        <section className="pb-20 mb-20">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Support Center
            </h1>
            <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
              Need help? Our support team is here for you 24/7. Reach out via email, phone, or the form below, and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Badge variant="default" className="px-3 py-1">
                <Clock className="w-3 h-3 mr-1 inline" />
                24/7 Support
              </Badge>
              <Badge variant="secondary" className="px-3 py-1">
                <Zap className="w-3 h-3 mr-1 inline" />
                Fast Response
              </Badge>
              <Badge variant="outline" className="px-3 py-1">
                <Users className="w-3 h-3 mr-1 inline" />
                Expert Team
              </Badge>
            </div>
          </div>
        </section>

        {/* Support Features */}
        <section className="mb-16">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Choose Our Support?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our dedicated support team combines technical expertise with genuine care to ensure your success with our platform.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {supportFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={index} className="p-6 text-center">
                    <Icon className="w-8 h-8 mx-auto mb-3 text-primary" />
                    <h3 className="font-bold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="mb-16">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <Card className="p-8">
                <div className="text-center space-y-4">
                  <Mail className="w-10 h-10 mx-auto text-primary" />
                  <h3 className="text-xl font-bold">Email Support</h3>
                  <p className="text-sm text-muted-foreground">
                    Get detailed help via email. Perfect for complex issues or when you need documentation.
                  </p>
                  <div className="border-t pt-4 space-y-2">
                    <p className="font-semibold">support@ommni.io</p>
                    <p className="text-sm text-muted-foreground">Response time: 1-2 hours</p>
                  </div>
                  <Button variant="outline" className="w-full" asChild>
                    <a href="mailto:support@ommni.io">
                      <Mail className="w-4 h-4 mr-2" />
                      Email Us
                    </a>
                  </Button>
                </div>
              </Card>

              <Card className="p-8">
                <div className="text-center space-y-4">
                  <Phone className="w-10 h-10 mx-auto text-primary" />
                  <h3 className="text-xl font-bold">Phone Support</h3>
                  <p className="text-sm text-muted-foreground">
                    Speak directly with our support team for immediate assistance and real-time problem solving.
                  </p>
                  <div className="border-t pt-4 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="country-select" className="text-sm font-medium">Select Country</Label>
                      <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                        <SelectTrigger id="country-select" className="w-full bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background z-50">
                          <SelectItem value="us">{phoneNumbers.us.label}</SelectItem>
                          <SelectItem value="au">{phoneNumbers.au.label}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="font-semibold">{phoneNumbers[selectedCountry as keyof typeof phoneNumbers].display}</p>
                    <p className="text-sm text-muted-foreground">Available 24/7</p>
                  </div>
                  <Button variant="outline" className="w-full" asChild>
                    <a href={`tel:${phoneNumbers[selectedCountry as keyof typeof phoneNumbers].number.replace(/\s/g, '')}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Call Us
                    </a>
                  </Button>
                </div>
              </Card>

              <Card className="p-8">
                <div className="text-center space-y-4">
                  <MessageCircle className="w-10 h-10 mx-auto text-primary" />
                  <h3 className="text-xl font-bold">Live Chat</h3>
                  <p className="text-sm text-muted-foreground">
                    Quick questions? Use our live chat for instant responses during business hours.
                  </p>
                  <div className="border-t pt-4 space-y-2">
                    <p className="text-sm text-muted-foreground">Mon-Fri 9AM-6PM EST</p>
                  </div>
                  <Button variant="outline" className="w-full" onClick={handleOpenChat}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Start Chat
                  </Button>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="p-8 max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <Send className="w-8 h-8 mx-auto mb-3 text-primary" />
                <h3 className="text-2xl font-bold mb-2">Send us a Message</h3>
                <p className="text-muted-foreground">
                  Fill out the form below and we'll get back to you as soon as possible. Please provide as much detail as possible to help us assist you better.
                </p>
              </div>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Please describe your issue or question in detail..."
                      rows={5}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg">
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8 space-y-4">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
                  <h4 className="text-xl font-bold text-green-600">Message Sent Successfully!</h4>
                  <p className="text-muted-foreground">
                    Thank you for contacting us. Our support team will review your message and get back to you within 2 hours.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Reference ID: #SUP-{Date.now().toString().slice(-6)}
                  </p>
                  <Button variant="outline" onClick={() => setSubmitted(false)}>
                    Send Another Message
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* Additional Resources */}
        <section className="mb-16">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <Card className="p-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-6 h-6 text-primary" />
                    <h3 className="text-xl font-bold">Documentation</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Comprehensive guides, API documentation, and tutorials to help you get the most out of our platform.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Getting Started Guide</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">API Reference</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Integration Examples</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Best Practices</span>
                    </li>
                  </ul>
                  <Button variant="outline" asChild>
                    <a href="https://docs.ommni.io" target="_blank" rel="noopener noreferrer">
                      View Documentation
                    </a>
                  </Button>
                </div>
              </Card>

              <Card className="p-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Headphones className="w-6 h-6 text-primary" />
                    <h3 className="text-xl font-bold">Premium Support</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Upgrade to premium support for priority assistance, dedicated account management, and enhanced SLA.
                  </p>
                  <div className="py-4 border-t border-b">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">$5,000</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">40 Prepaid Billable Support Hours</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Priority Queue Access</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Dedicated Account Manager</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">30-minute Response SLA</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Phone & Video Support</span>
                    </li>
                  </ul>
                  <Button 
                    variant="default"
                    onClick={() => window.location.href = 'mailto:sales@ommni.io'}
                  >
                    Upgrade Support
                  </Button>
                </div>
              </Card>
            </div>

            {/* FAQ Section */}
            <Card className="p-8">
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Find quick answers to common questions. Can't find what you're looking for? Contact our support team.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {faqs.map((faq, index) => (
                    <Card key={index} className="p-4 border-2">
                      <h4 className="font-bold text-primary mb-2">{faq.question}</h4>
                      <p className="text-sm text-muted-foreground">{faq.answer}</p>
                    </Card>
                  ))}
                </div>
                <div className="text-center hidden">
                  <Button variant="outline">
                    View All FAQs
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
}
